// lib/failed-generation.ts
//
// The List grid's failed-generation tile: reason wording and retry-task derivation.
// Lives in lib/ so both are unit-testable: the component that uses them pulls the whole
// application graph and does not load under jest, same reason as lib/launch-guard.ts.
import type { GenerationTask, GenerationTaskSnapshot } from '#types/types';
import { terminalImageGenerationFailureCode, readImageGenerationFailureDetails } from '#lib/geminiImageFailure';
import { LIST_GENERATION_ATTEMPT_LIMIT } from '#lib/list/listRetryAllowance';
import { LIST_RECOVERY_EXHAUSTED } from '#lib/list/listGenerationRepair';

/** Input is newest first, including successes and pending attempts so predecessors stay hidden. */
export const restoreListTasks = (snapshots: Array<Omit<GenerationTaskSnapshot, 'createdAt' | 'finalizedAt'> & { createdAt: string | Date; finalizedAt?: string | Date | null }>): GenerationTask[] => {
  const seen = new Set<string>();
  return snapshots.flatMap(snapshot => {
    const presentation = snapshot.finalMetadata?.listPresentation as Record<string, unknown> | undefined;
    const rootTaskId = typeof presentation?.rootTaskId === 'string' && presentation.rootTaskId.trim() ? presentation.rootTaskId : snapshot.clientTaskId || snapshot.id;
    if (!rootTaskId || seen.has(rootTaskId)) return [];
    seen.add(rootTaskId);
    const inputs = snapshot.finalMetadata?.listInputs as { main?: string | null; urls?: string[]; mask?: string | null } | undefined;
    const params = snapshot.params as unknown as Record<string, unknown> | null;
    const snapshotCreatedAt = new Date(snapshot.createdAt).getTime();
    const failed = snapshot.taskStatus === 'FAILED';
    const pending = ['SPAWNED', 'RUNNING', 'SAVING', 'SAVE_PENDING'].includes(snapshot.taskStatus ?? '');
    if ((!failed && !pending) || snapshot.isOutcomeReported || snapshot.layerId
      || (failed && (snapshot.finalAssetUrls?.length || snapshot.type !== 'diffuse' || !inputs
        || Number(params?.num_images ?? 1) !== 1 || snapshot.operation || params?.factoryRunId))
      || (snapshot.type !== 'diffuse' && snapshot.type !== 'film') || snapshot.workspaceId || !snapshot.projectId || !snapshot.chatSessionId
      || !params || typeof params !== 'object' || Array.isArray(params)
      || !snapshot.rect || !Number.isFinite(snapshot.rect.w) || snapshot.rect.w <= 0 || !Number.isFinite(snapshot.rect.h) || snapshot.rect.h <= 0
      || !Number.isFinite(snapshot.targetWidth) || snapshot.targetWidth! <= 0 || !Number.isFinite(snapshot.targetHeight) || snapshot.targetHeight! <= 0) return [];
    const startedAt = typeof presentation?.startedAt === 'number' && Number.isFinite(presentation.startedAt) && presentation.startedAt > 0
      ? presentation.startedAt : new Date(snapshot.createdAt).getTime();
    if (!Number.isFinite(startedAt) || startedAt <= 0 || !Number.isFinite(snapshotCreatedAt) || snapshotCreatedAt <= 0) return [];
    return [{
      id: snapshot.clientTaskId ?? snapshot.id, serverTaskId: snapshot.id, projectId: snapshot.projectId,
      chatSessionId: snapshot.chatSessionId, workspaceId: '', outputScope: { kind: 'PROJECT', projectId: snapshot.projectId },
      type: snapshot.type, model: snapshot.modelName, params: snapshot.params!, rect: snapshot.rect,
      targetWidth: snapshot.targetWidth!, targetHeight: snapshot.targetHeight!,
      mergeStrategy: (snapshot.mergeStrategy ?? 'addInList') as GenerationTask['mergeStrategy'],
      operation: snapshot.operation ?? undefined, file_data: snapshot.fileData ?? undefined,
      segmentationPrompt: snapshot.segmentationPrompt ?? undefined,
      gridSubdivisionsX: snapshot.gridSubdivisionsX ?? undefined, gridSubdivisionsY: snapshot.gridSubdivisionsY ?? undefined,
      upscaleFactor: snapshot.upscaleFactor ?? 1, blurRadius: snapshot.blurRadius ?? 0, layerContextSnapshot: null,
      mainImagePrivateUrl: snapshot.mainImagePrivateUrl, maskPrivateUrl: snapshot.maskPrivateUrl,
      extraImageUrlPrivate: snapshot.extraImageUrlPrivate, referenceImagePrivateUrls: snapshot.referenceImagePrivateUrls,
      mainImagePublicUrl: inputs?.main ?? undefined, urls: inputs?.urls, maskUrl: inputs?.mask ?? undefined,
      ...(failed ? { error: snapshot.failureMessage || 'The model did not produce an image.', failureCode: terminalImageGenerationFailureCode(snapshot.finalMetadata), failureDetails: readImageGenerationFailureDetails(snapshot.finalMetadata) } : {}),
      listPresentation: { batchKey: typeof presentation?.batchKey === 'string' && presentation.batchKey ? presentation.batchKey : rootTaskId,
        snapshotCreatedAt },
      isAgentSpawned: presentation?.isAgentSpawned === true,
      startedAt, listAttempt: { rootTaskId, startedAt, number: typeof presentation?.number === 'number' && Number.isInteger(presentation.number) && presentation.number > 0 ? presentation.number : 1,
        limit: LIST_GENERATION_ATTEMPT_LIMIT, active: false },
    }];
  });
};

/** Keep already-compiled routes valid while the preview hot-reloads this module. */
export const restoreFailedListTasks: typeof restoreListTasks = snapshots => restoreListTasks(snapshots).filter(task => Boolean(task.error));

/**
 * Turn a task's failure into a plain-words reason for the tile. `GenerationTask.error`
 * carries `getSpecificErrorMessage(originalError)`, which PREFERS the provider's own
 * `response.data.message`, so raw vendor text (provider names, stack fragments, policy
 * jargon) can and does reach the client. This maps it onto a small closed set of product
 * phrases and never echoes the raw string. When in doubt: a technical error.
 */
/** A launch the server never recorded after this long cannot start any more (Factory preparation caps at 3 min). */
export const NEVER_STARTED_AFTER_MS = 5 * 60_000;
export const GENERATION_NEVER_STARTED_MESSAGE = 'This generation never started and nothing was charged. You can retry it.';

export const describeGenerationFailure = (rawError: unknown): string => {
  const text =
    typeof rawError === 'string'
      ? rawError
      : rawError instanceof Error
        ? rawError.message
        : '';
  const t = text.toLowerCase();

  if (text === 'The image provider is temporarily rate limited.' || text === 'The image provider is temporarily unavailable.') return 'The image service is temporarily busy. Your request is kept.';
  if (text === 'The image provider rejected access. Check the provider credentials and deployment permissions.') return 'Image service access needs attention. Your request is kept.';
  if (text === 'The image provider rejected the request format or parameters.' || text === 'The image provider rejected this request. Your brief and references are kept.') return 'The model could not accept this request. Your brief and references are kept.';
  if (text === 'The image request timed out. Check its saved status before generating again.') return 'The response was delayed. Check this generation before retrying.';
  if (text === 'The generated image could not be retrieved. Check its saved status before generating again.' || text === 'The generated image could not be saved. Recover the existing result before generating again.') return 'The image needs recovery. Check this result before generating again.';
  if (text === 'The returned image did not meet the required output format.') return 'The returned image could not be used in the requested format.';

  if (text === LIST_RECOVERY_EXHAUSTED || t.startsWith('this attempt did not produce an image.')) return 'No usable image yet. Your brief and references are kept.';
  if (t.startsWith('mai cannot preserve the requested resolution') || t.startsWith('mai cannot preserve these exact output dimensions')) return 'MAI cannot keep this format. Your brief and references are kept; choose a compatible model.';
  if (t.startsWith('mai cannot preserve this edit region') || t.startsWith('mai cannot combine this edit source')) return 'MAI cannot keep this edit and its references. Choose a compatible editing model.';
  if (t.startsWith('mai cannot preserve all of this request')) return 'MAI cannot keep this reference combination. Choose a multi-reference model.';
  if (t.startsWith('mai needs a source image')) return 'MAI needs a source image or a ready persona or object.';
  if (t.startsWith('i am checking the previous attempt') || t.startsWith('the request changed while i checked')) return 'Checking the previous attempt before starting another.';
  if (t.startsWith('your brief and references are kept, but the available credits')) return 'Your request is kept. Check the team budget before continuing.';
  if (t.startsWith('this model is unavailable under the current plan')) return 'Your request is kept. Check model access and available credits before continuing.';

  if (/ten attempts could not produce|retry budget is exhausted/.test(t)) return 'Could not produce this image. Change the brief or references before trying again.';
  if (/troubleshooting (?:could not finish|stopped)|no distinct correction/.test(t)) return 'Troubleshooting could not finish. Review the brief or references and retry.';
  if (/original (?:settings or references changed|input images cannot be verified)/.test(t)) return 'The references or settings changed. Send a new request to use them.';

  if (text === GENERATION_NEVER_STARTED_MESSAGE) return text;
  if (/generation status unavailable/.test(t)) return 'Connection interrupted. Check status before trying again.';
  if (/generation submission not confirmed/.test(t)) return 'Start not confirmed. Check status before trying again.';
  if (/generation status is unconfirmed|generation is still running on the server/.test(t)) return 'Status unconfirmed. Check status before trying again.';
  if (/api key is invalid|invalid api key/.test(t)) return 'This model is unavailable right now. Choose another model.';
  if (/unauthorized|status code 401/.test(t)) return 'Your session could not be verified. Refresh the page and retry.';
  if (/likenesses? of real people/.test(t)) return 'This model refused an image of a realistic person. Choose another video model, such as Kling 3.';
  if (/model blocked image generation/.test(t)) return 'The model blocked this generation. Review the prompt or reference photos before trying again.';
  if (/model finished without producing an image|model returned no image|model returned text instead of an image/.test(t)) return 'The model did not produce an image.';

  if (/nsfw|flagged|moderation|content policy|safety (?:system|check|filter)|sensitive content|risk control|prohibited content/.test(t)) {
    return 'The content was flagged by safety checks. Adjust the prompt or image and retry.';
  }
  // 403 is the credits refusal on generation routes (the task pipeline calls
  // handleNotEnoughCreditsError on it), and axios collapses it to "status code 403".
  if (/not enough credits|insufficient credit|quota|payment required|status code 402|status code 403/.test(t)) {
    return 'There were not enough credits to finish this generation.';
  }
  if (/timed? ?out|timeout|deadline exceeded|took too long/.test(t)) {
    return 'The generation took too long and was stopped.';
  }
  return 'The generation hit a technical error.';
};

/**
 * Provider text that must never reach a user. Matched on the things that identify a THIRD PARTY or
 * its account state - a vendor domain, billing wording, key/account status - not on model ids,
 * which are product surface and appear legitimately in our own messages ("ideogram-inpaint requires
 * a non-empty prompt").
 *
 * Reported 2026-09-02: a generation failure surfaced verbatim as
 *   "User is locked. Reason: Exhausted balance. Top up your balance at fal.ai/dashboard/billing."
 * That names our supplier, exposes our account state, and tells the user to pay a bill that is not
 * theirs. A model that fails should just fail, in our own words.
 */
const THIRD_PARTY_LEAK =
  /(?:https?:\/\/)?(?:[a-z0-9-]+\.)?(?:fal\.(?:ai|run)|replicate\.com|openai\.com|anthropic\.com|bfl\.ai|topazlabs\.com|magnific\.ai|ideogram\.ai|recraft\.ai|luma(?:labs)?\.ai)\b|\bdashboard\/billing\b|\btop ?up\b|\bexhausted balance\b|\binsufficient balance\b|\buser is locked\b|\bbilling\b|\b(?:api|secret) key\b/i;

/**
 * The last gate before an error string is shown to a user. Our own copy passes through untouched;
 * anything carrying supplier identity or account state is replaced by a product phrase from
 * describeGenerationFailure, which is a closed set and never echoes the input.
 */
// Transport wording (axios, fetch) names no action a user can take; the sentence it was appended
// to often does, so only the fragment goes.
const TRANSPORT_FRAGMENT = /[\s:,-]*\b(?:Network Error|Request failed with status code \d{3}|timeout of \d+ms exceeded|socket hang up|ECONNRESET|Failed to fetch)\b\.?/gi;

const stripTransportText = (text: string): string => {
  const stripped = text.replace(TRANSPORT_FRAGMENT, '').trim();
  if (stripped === text) return text;
  if (!stripped) return 'The connection was interrupted. Please try again.';
  return /[.!?]$/.test(stripped) ? stripped : `${stripped}.`;
};

export const sanitizeUserFacingError = (rawError: unknown): string => {
  const text =
    typeof rawError === 'string'
      ? rawError
      : rawError instanceof Error
        ? rawError.message
        : '';
  const trimmed = text.trim();
  if (!trimmed) return 'Something went wrong.';
  if (!THIRD_PARTY_LEAK.test(trimmed)) return stripTransportText(trimmed);
  return describeGenerationFailure(trimmed);
};

/** The subset of GenerationTask the retry derivation touches. Structural on purpose:
 *  the full type lives in types/types.ts and pulling it here would drag app types into
 *  a leaf module (same trade-off as PendingLaunchTask in lib/launch-guard.ts). */
export interface RetryableGenerationTask {
  id: string;
  listPresentation?: GenerationTask['listPresentation'];
  error?: string;
  failureCode?: string;
  failureDetails?: GenerationTask['failureDetails'];
  isOutcomeReported?: boolean;
  startedAt?: number;
  lastStatusConfirmedAt?: number;
  serverTaskId?: string | null;
  loopTier?: 'direction' | 'motion' | 'render' | null;
  loopStepLabel?: string | null;
  loopEtaSeconds?: number | null;
  loopProgress?: number | null;
}

/**
 * Derive the relaunch task from a failed one. The failed task IS the fully built launch
 * payload every queue path hands to processTasks (params incl. seed, uploaded source
 * URLs, rect, model, sourceListOutputId...), so re-running a copy of it through
 * processTasks reuses the exact original parameters, with no re-render and no drift from
 * whatever the workspace looks like now. Only the run-lifecycle fields are reset:
 * a fresh id, no error, no reported outcome, no server task, no stale loop telemetry,
 * and no startedAt (processTasks stamps the retry's own start, keeping the pending
 * tile's clock truthful). The original object is never mutated.
 */
export const buildGenerationRetryTask = <T extends RetryableGenerationTask>(
  failedTask: T,
  newTaskId: string,
): T => {
  const retry: T = {
    ...failedTask,
    id: newTaskId,
    isOutcomeReported: false,
    serverTaskId: null,
    loopTier: null,
    loopStepLabel: null,
    loopEtaSeconds: null,
    loopProgress: null,
  };
  delete retry.error;
  delete retry.failureCode;
  delete retry.failureDetails;
  delete retry.startedAt;
  delete retry.lastStatusConfirmedAt;
  if (failedTask.listPresentation) retry.listPresentation = { batchKey: failedTask.listPresentation.batchKey };
  return retry;
};
